import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Alert, Spinner } from 'react-bootstrap';
import { Phone, Mail, ArrowRight } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { authAPI } from '../../api/auth';
import Logo from '../../components/Logo';
import TermsCheckbox from '../../components/TermsCheckbox';
import { useFeatureFlag } from '../../contexts/FeatureFlagContext';
import '../../styles/blinkit.css';

type Step = 'identifier' | 'otp';
type IdentifierType = 'phone' | 'email';

const Login: React.FC = () => {
  const [step, setStep] = useState<Step>('identifier');
  const [identifierType, setIdentifierType] = useState<IdentifierType>('phone');
  const [identifier, setIdentifier] = useState('');
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [devOtp, setDevOtp] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const navigate = useNavigate();
  const { setUser, setTokens } = useAuthStore();
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const storesEnabled = useFeatureFlag('stores');
  const deliveriesEnabled = useFeatureFlag('deliveries');

  // Countdown timer for resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleRequestOTP = async () => {
    if (!identifier.trim()) {
      setError(identifierType === 'phone' ? 'Please enter your phone number' : 'Please enter your email');
      return;
    }
    if (!agreedToTerms) {
      setError('Please accept the Terms & Conditions to continue');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await authAPI.requestOTP({
        identifier: identifier.trim(),
        identifier_type: identifierType,
      });
      
      // In dev mode, auto-fill OTP. Belt-and-braces: only honor dev_otp when
      // the frontend itself is built in development (NODE_ENV !== 'production'),
      // so even if a misconfigured prod backend leaked it, the prod UI ignores it.
      if (response.dev_otp && process.env.NODE_ENV !== 'production') {
        setDevOtp(response.dev_otp);
      }
      
      setStep('otp');
      setCountdown(30);
      // Focus first OTP input
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (err: any) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 503) {
        setError(detail || 'OTP service is temporarily unavailable. Please try again later.');
      } else if (status === 429) {
        setError(detail || 'Too many OTP requests. Please wait and try again.');
      } else {
        setError(detail || 'Failed to send OTP. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = (index: number, value: string) => {
    if (value.length > 1) {
      // Handle paste
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newOtp = [...otpDigits];
      digits.forEach((d, i) => {
        if (index + i < 6) newOtp[index + i] = d;
      });
      setOtpDigits(newOtp);
      const nextFocus = Math.min(index + digits.length, 5);
      otpRefs.current[nextFocus]?.focus();
      
      // Auto-verify if all digits filled
      if (newOtp.every(d => d)) {
        verifyOTP(newOtp.join(''));
      }
      return;
    }

    const newOtp = [...otpDigits];
    newOtp[index] = value.replace(/\D/g, '');
    setOtpDigits(newOtp);

    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }

    // Auto-verify when all filled
    if (newOtp.every(d => d)) {
      verifyOTP(newOtp.join(''));
    }
  };

  const handleOTPKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const verifyOTP = async (otp: string) => {
    setLoading(true);
    setError('');

    try {
      const response = await authAPI.verifyOTP({
        identifier: identifier.trim(),
        identifier_type: identifierType,
        otp_code: otp,
      });

      // Store tokens and user info
      setTokens(response.access_token, response.refresh_token);
      setUser(response.user);

      // Redirect based on role
      const role = response.user?.role;
      if (role === 'store') navigate('/store-dashboard');
      else if (role === 'delivery_agent') navigate('/deliveries');
      else if (role === 'admin') navigate('/admin');
      else if (response.is_new_user) navigate('/profile?welcome=1');
      else navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Invalid OTP');
      setOtpDigits(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = () => {
    if (countdown > 0) return;
    setOtpDigits(['', '', '', '', '', '']);
    handleRequestOTP();
  };

  return (
    <div className="otp-login-page">
      <div className="otp-login-card">
        {/* Brand */}
        <div className="brand-logo" style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
          <Logo size={72} iconSize={180} iconCropScale={1.25} variant="stacked" tagline />
        </div>

        {error && (
          <Alert variant="danger" className="py-2 px-3" style={{ fontSize: '13px', borderRadius: '10px' }}>
            {error}
          </Alert>
        )}

        {step === 'identifier' ? (
          <>
            {/* Tab Switcher */}
            <div className="tab-switcher">
              <button
                className={identifierType === 'phone' ? 'active' : ''}
                onClick={() => { setIdentifierType('phone'); setIdentifier(''); setError(''); }}
              >
                <Phone size={14} className="me-1" /> Phone
              </button>
              <button
                className={identifierType === 'email' ? 'active' : ''}
                onClick={() => { setIdentifierType('email'); setIdentifier(''); setError(''); }}
              >
                <Mail size={14} className="me-1" /> Email
              </button>
            </div>

            {/* Input */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px', display: 'block', color: '#333' }}>
                {identifierType === 'phone' ? 'Phone Number' : 'Email Address'}
              </label>
              {identifierType === 'phone' ? (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <div style={{
                    background: '#F0F0F5',
                    borderRadius: '10px',
                    padding: '12px 14px',
                    fontWeight: 600,
                    fontSize: '15px',
                    color: '#333',
                  }}>
                    +91
                  </div>
                  <input
                    type="tel"
                    placeholder="Enter phone number"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    style={{
                      flex: 1,
                      background: '#F0F0F5',
                      border: 'none',
                      borderRadius: '10px',
                      padding: '12px 14px',
                      fontSize: '15px',
                      outline: 'none',
                    }}
                    maxLength={10}
                    autoFocus
                  />
                </div>
              ) : (
                <input
                  type="email"
                  placeholder="Enter email address"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  style={{
                    width: '100%',
                    background: '#F0F0F5',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '12px 14px',
                    fontSize: '15px',
                    outline: 'none',
                  }}
                  autoFocus
                />
              )}
            </div>

            {/* Terms consent — required */}
            <TermsCheckbox
              checked={agreedToTerms}
              onChange={setAgreedToTerms}
              compact
              className="mb-3"
            />

            {/* CTA */}
            <button
              className="green-btn"
              onClick={handleRequestOTP}
              disabled={loading || !identifier.trim() || !agreedToTerms}
            >
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>Get OTP <ArrowRight size={18} className="ms-1" /></>
              )}
            </button>

            <p style={{ textAlign: 'center', margin: '12px 0 0', fontSize: '11px', color: '#999' }}>
              By continuing you accept the Terms above and our Privacy Policy.
            </p>

            {/* Store/Delivery registration link — shown only for enabled roles */}
            {(storesEnabled || deliveriesEnabled) && (() => {
              let label: string;
              let to: string;
              if (storesEnabled && deliveriesEnabled) {
                label = 'Register as Store Owner / Delivery Agent →';
                to = '/signup';
              } else if (storesEnabled) {
                label = 'Register as Store Owner →';
                to = '/register/store';
              } else {
                label = 'Register as Delivery Agent →';
                to = '/register/delivery';
              }
              return (
                <div style={{ textAlign: 'center', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #eee' }}>
                  <Link to={to} style={{ color: '#0C831F', fontWeight: 600, fontSize: '13px', textDecoration: 'none' }}>
                    {label}
                  </Link>
                </div>
              );
            })()}
          </>
        ) : (
          <>
            {/* OTP Verification */}
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>
                Enter the 6-digit code sent to
              </p>
              <p style={{ fontSize: '15px', fontWeight: 700, color: '#333', margin: '4px 0' }}>
                {identifierType === 'phone' ? `+91 ${identifier}` : identifier}
              </p>
              <button
                onClick={() => { setStep('identifier'); setError(''); setOtpDigits(['', '', '', '', '', '']); }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#0C831F',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                Change
              </button>
            </div>

            {/* Dev OTP hint - shown when backend returns it (dev environment only) */}
            {devOtp && (
              <Alert variant="info" className="py-1 px-3 text-center" style={{ fontSize: '12px', borderRadius: '8px' }}>
                Dev OTP: <strong>{devOtp}</strong>
              </Alert>
            )}

            {/* OTP Input */}
            <div className="otp-input-group">
              {otpDigits.map((digit, i) => (
                <input
                  key={i}
                  ref={el => otpRefs.current[i] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={digit}
                  onChange={(e) => handleOTPChange(i, e.target.value)}
                  onKeyDown={(e) => handleOTPKeyDown(i, e)}
                  onPaste={(e) => {
                    e.preventDefault();
                    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
                    handleOTPChange(0, pasted);
                  }}
                  autoFocus={i === 0}
                />
              ))}
            </div>

            {/* Verify Button */}
            <button
              className="green-btn"
              onClick={() => verifyOTP(otpDigits.join(''))}
              disabled={loading || otpDigits.some(d => !d)}
              style={{ marginBottom: '16px' }}
            >
              {loading ? <Spinner animation="border" size="sm" /> : 'Verify & Login'}
            </button>

            {/* Resend */}
            <div style={{ textAlign: 'center' }}>
              {countdown > 0 ? (
                <span style={{ fontSize: '13px', color: '#999' }}>
                  Resend OTP in <strong>{countdown}s</strong>
                </span>
              ) : (
                <button
                  onClick={handleResend}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#0C831F',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Resend OTP
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Login;
